/**
 * Who is in, in the top bar.
 *
 * "is it possible to see at the top right all who are blocked in? For everyone
 * so we know who is in" — clocked in. The time clock already knew; it was only
 * visible on the front desk page, which is no use when you are in a chart and
 * want to know whether the nurse is on site.
 *
 * Avatars rather than a number, because the question is never "how many" - it
 * is "is Sandy in". Each carries the colour assigned to that person, so the
 * same face is the same shade here, in chat and on their record.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Avatar } from "@/components/ui/Avatar";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { hoursLabel, type Shift } from "./TimeClock";

const POLL_MS = 60_000;

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export function OnShift() {
  const [on, setOn] = useState<Shift[] | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const load = useCallback(() => {
    api.get<{ on: Shift[] }>("/staff/clock/who")
      .then((d) => setOn(d.on))
      .catch(() => setOn(null));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Nobody on the clock is not worth a slot in the chrome.
  if (!on || on.length === 0) return null;

  const shown = on.slice(0, 3);
  const more = on.length - shown.length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-on-shift
        onClick={() => setOpen((o) => !o)}
        aria-label={`${on.length} clocked in`}
        aria-expanded={open}
        className={cn(
          "flex items-center gap-1 rounded-full py-0.5 pl-1 pr-2 transition-colors",
          "hover:bg-zinc-800/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400",
          open && "bg-zinc-800/60",
        )}
      >
        <span className="flex -space-x-2">
          {shown.map((s) => (
            <Avatar
              key={s.id}
              name={s.staff_name}
              color={s.color}
              size="xs"
              className="ring-2 ring-zinc-950"
            />
          ))}
        </span>
        {more > 0 && <span className="text-[11px] font-semibold text-zinc-400">+{more}</span>}
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" aria-hidden />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 w-64 max-w-[92vw] animate-fade-in rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl shadow-black/40">
          <div className="border-b border-zinc-800 px-3 py-2">
            <p className="text-sm font-semibold text-zinc-100">
              {on.length === 1 ? "1 person is in" : `${on.length} people are in`}
            </p>
          </div>
          <ul className="p-1">
            {on.map((s) => (
              <li key={s.id} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
                <Avatar name={s.staff_name} color={s.color} size="xs" />
                <span className="min-w-0 flex-1 truncate text-[13px] text-zinc-200">
                  {s.staff_name ?? "Someone"}
                  {s.credential && <span className="ml-1 text-zinc-500">{s.credential}</span>}
                </span>
                <span className="shrink-0 text-[11px] tabular-nums text-zinc-500">
                  since {timeOf(s.started_at)}
                </span>
              </li>
            ))}
          </ul>
          <p className="border-t border-zinc-800 px-3 py-2 text-[11px] text-zinc-500">
            Total on the clock right now:{" "}
            {hoursLabel(on.reduce((sum, s) => sum + (Date.now() - new Date(s.started_at).getTime()) / 3_600_000, 0))}
          </p>
        </div>
      )}
    </div>
  );
}
